import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flag, ChevronsRight, Gift } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { setPageTitle } from "../lib/format";
import "../styles/spin.css";

const CarSilhouette = ({ color, testId }) => (
  <svg viewBox="0 0 120 44" className="w-full h-auto" aria-hidden="true" data-testid={testId}>
    <path d="M8 32 L15 23 Q31 13 47 13 L71 13 Q87 13 97 21 L109 25 Q115 27 115 32 L115 34 L8 34 Z" fill={color} />
    <path d="M47 15 L69 15 L79 21 L47 21 Z" fill="#080B0D" opacity="0.7" />
    <circle cx="35" cy="34" r="7.5" fill="#080B0D" stroke={color} strokeWidth="2.5" />
    <circle cx="89" cy="34" r="7.5" fill="#080B0D" stroke={color} strokeWidth="2.5" />
  </svg>
);

const CARS = [
  { key: "a", color: "#3F3F46", lane: "spin-car-a" },
  { key: "b", color: "#E50914", lane: "spin-car-b" },   // the winner — MyRedPark red
  { key: "c", color: "#71717A", lane: "spin-car-c" },
];

export default function SpinReveal() {
  const { user, loading: authLoading } = useAuth();
  const [reward, setReward] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading → intro → ready → racing → reveal
  const navigate = useNavigate();
  const reduced = useRef(
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => setPageTitle("Your Reward"), []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    // Fetch the SERVER-assigned value without marking it revealed — only the actual
    // reveal phase marks it, so a bounce mid-flow re-offers the experience on login.
    api.spinReveal(false)
      .then((r) => {
        if (!r.reward) { navigate("/dashboard", { replace: true }); return; }
        setReward(r.reward);
        setPhase(reduced.current ? "reveal" : "intro"); // accessible non-animated path
      })
      .catch(() => navigate("/dashboard", { replace: true }));
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (phase === "reveal" && reward) {
      api.spinReveal(true).catch(() => {}); // mark revealed exactly when the value is shown
    }
  }, [phase, reward]);

  useEffect(() => {
    if (phase !== "intro") return;
    const t = setTimeout(() => setPhase("ready"), 1600);
    return () => clearTimeout(t);
  }, [phase]);

  const startRace = () => {
    if (reduced.current) { setPhase("reveal"); return; }
    setPhase("racing");
    setTimeout(() => setPhase("reveal"), 3400);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#080B0D] overflow-hidden" data-testid="spin-reveal-page">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 45% at 50% 0%, rgba(229,9,20,.16), transparent 70%)" }} />
      <div className={`spin-stage relative h-full flex flex-col items-center justify-center px-6 phase-${phase}`}>

        {phase === "intro" && (
          <p className="font-display font-bold text-4xl sm:text-6xl tracking-tight spin-youre-in" data-testid="spin-intro">
            YOU&rsquo;RE IN.
          </p>
        )}

        {phase === "ready" && (
          <div className="text-center spin-fade-up" data-testid="spin-ready">
            <Gift className="w-10 h-10 mx-auto text-[#E50914]" />
            <p className="font-display font-bold text-3xl sm:text-5xl tracking-tight mt-6">Your first-booking reward is ready.</p>
            <p className="text-sm text-zinc-500 mt-4">Three cars. One finish line. Your reward is already locked in.</p>
            <button onClick={startRace} data-testid="spin-start-btn" className="btn-primary mt-8 !h-12 px-10">
              Start engines <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {(phase === "racing" || phase === "reveal") && (
          <div className="absolute inset-0" data-testid="spin-track">
            <div className="spin-road" />
            <div className="spin-lanes" />
            <div className="spin-finish" data-testid="spin-finish-line"><Flag className="w-5 h-5 text-white/70" /></div>
            {CARS.map((c) => (
              <div key={c.key} className={`spin-car ${c.lane}`} data-testid={c.key === "b" ? "spin-car-winner" : `spin-car-${c.key}`}>
                <CarSilhouette color={c.color} />
              </div>
            ))}
          </div>
        )}

        {phase === "reveal" && reward && (
          <div className="relative z-10 text-center spin-reveal-pop" data-testid="reward-reveal">
            <div className="spin-confetti" aria-hidden="true">{Array.from({ length: 14 }).map((_, i) => <span key={i} />)}</div>
            <p className="section-label">Your first-booking reward</p>
            <p className="font-display font-bold tracking-tight mt-3 spin-reward-glow"
               style={{ fontSize: "clamp(4.5rem, 16vw, 9rem)", lineHeight: 1 }}
               data-testid="reward-percent">{reward.discount_percent}%</p>
            <p className="text-zinc-400 text-sm mt-3">off the parking on your first eligible booking</p>
            <Link to="/find" data-testid="reward-cta" className="btn-primary mt-8 !h-12 px-8 inline-flex">
              Use {reward.discount_percent}% OFF on your first booking
            </Link>
            <div>
              <Link to="/dashboard" data-testid="spin-dashboard-link" className="inline-block mt-5 text-xs text-zinc-600 hover:text-zinc-300 transition-colors">
                Go to dashboard
              </Link>
            </div>
          </div>
        )}

        {phase === "loading" && <p className="text-zinc-600 text-sm">Preparing your reward…</p>}
      </div>
    </div>
  );
}
